import React from 'react';
import { useTranslation } from 'react-i18next';
import { Icon } from '@iconify/react';
import { Chip } from '@heroui/react';
import { EmptyState, NumberValue, Widget } from '@heroui-pro/react';
import { BarChart } from '@heroui-pro/react/bar-chart';
import { Rectangle } from 'recharts';
import type { BarShapeProps } from 'recharts';
import type { HourlyActivity, HourlyActivityBucket } from '@/api/models';
import { formatCurrencyValue } from '@/utils/currency';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';
import { HourlyActivityTooltip } from './HourlyActivityTooltip';
import {
    BAR_CHART_HEIGHT_PX,
    TREND_CHIP_COLOR,
    TREND_CHIP_ICON,
    formatHourLabel,
    getPercentageTrend,
    toFractionalPercent,
} from './hourlyActivityMeta';

type HourlyActivityViewProps = {
    data: HourlyActivity;
};

export const HourlyActivityView: React.FC<HourlyActivityViewProps> = ({
    data,
}) => {
    const { t } = useTranslation('pages');

    const isEmpty = data.hours.length === 0;

    const copy = React.useMemo(
        () => ({
            title: t('sales.widgets.hourlyActivity.title'),
            subtitle: t('sales.widgets.hourlyActivity.subtitle'),
            trendAria: t('sales.widgets.hourlyActivity.trendAria'),
            emptyTitle: t('sales.widgets.hourlyActivity.empty.title'),
            emptyDescription: t(
                'sales.widgets.hourlyActivity.empty.description',
            ),
        }),
        [t],
    );

    // Server provides the peak set; we keep it as a `Set<number>` so the
    // per-bar shape lookup can do an O(1) check.
    const peakHours = React.useMemo(
        () => new Set(data.peakHours),
        [data.peakHours],
    );

    const formatRevenue = React.useCallback(
        (value: number | string) => formatCurrencyValue(data.currency, value),
        [data.currency],
    );

    const formatHour = React.useCallback(
        (value: number | string) => formatHourLabel(Number(value)),
        [],
    );

    const renderBar = React.useCallback(
        (barProps: BarShapeProps) => {
            const bucket = barProps.payload as HourlyActivityBucket;
            return (
                <Rectangle
                    {...barProps}
                    fill={
                        peakHours.has(bucket.hour)
                            ? 'var(--success)'
                            : 'color-mix(in oklab, var(--success), transparent 55%)'
                    }
                />
            );
        },
        [peakHours],
    );

    const trend = getPercentageTrend(data.changePercentage);

    return (
        <section
            aria-labelledby="hourly-activity-widget-title"
            {...getNodeTestIdProp('hourly-activity-section')}
        >
            <Widget
                className="flex flex-col gap-4"
                {...getNodeTestIdProp('hourly-activity-widget')}
            >
                <Widget.Header
                    className="mt-4"
                    {...getNodeTestIdProp('hourly-activity-widget-header')}
                >
                    <Widget.Title
                        className="flex items-center gap-2"
                        {...getNodeTestIdProp('hourly-activity-widget-title')}
                    >
                        <div
                            aria-hidden
                            className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent"
                            {...getNodeTestIdProp('hourly-activity-icon-wrap')}
                        >
                            <Icon
                                className="size-4"
                                icon="solar:clock-circle-bold-duotone"
                                {...getNodeTestIdProp('hourly-activity-icon')}
                            />
                        </div>
                        {copy.title}
                        {trend !== null && data.changePercentage !== null ? (
                            <Chip
                                aria-label={copy.trendAria}
                                className="ms-auto inline-flex items-center gap-1"
                                color={TREND_CHIP_COLOR[trend]}
                                size="sm"
                                variant="tertiary"
                                {...getNodeTestIdProp('hourly-activity-trend')}
                            >
                                <Icon
                                    aria-hidden
                                    className="size-3"
                                    icon={TREND_CHIP_ICON[trend]}
                                    {...getNodeTestIdProp(
                                        'hourly-activity-trend-icon',
                                    )}
                                />
                                <NumberValue
                                    maximumFractionDigits={2}
                                    minimumFractionDigits={0}
                                    signDisplay="exceptZero"
                                    style="percent"
                                    value={toFractionalPercent(
                                        data.changePercentage,
                                    )}
                                    {...getNodeTestIdProp(
                                        'hourly-activity-trend-value',
                                    )}
                                />
                            </Chip>
                        ) : null}
                    </Widget.Title>
                    <Widget.Description
                        {...getNodeTestIdProp(
                            'hourly-activity-widget-subtitle',
                        )}
                    >
                        {copy.subtitle}
                    </Widget.Description>
                </Widget.Header>
                <Widget.Content
                    className={
                        isEmpty
                            ? 'flex min-h-55 items-center justify-center'
                            : undefined
                    }
                >
                    {isEmpty ? (
                        <EmptyState
                            {...getNodeTestIdProp('hourly-activity-empty')}
                        >
                            <EmptyState.Header>
                                <EmptyState.Media variant="icon">
                                    <Icon
                                        aria-hidden
                                        className="size-6"
                                        icon="solar:clock-circle-bold-duotone"
                                        {...getNodeTestIdProp(
                                            'hourly-activity-empty-icon',
                                        )}
                                    />
                                </EmptyState.Media>
                                <EmptyState.Title
                                    {...getNodeTestIdProp(
                                        'hourly-activity-empty-title',
                                    )}
                                >
                                    {copy.emptyTitle}
                                </EmptyState.Title>
                                <EmptyState.Description
                                    {...getNodeTestIdProp(
                                        'hourly-activity-empty-description',
                                    )}
                                >
                                    {copy.emptyDescription}
                                </EmptyState.Description>
                            </EmptyState.Header>
                        </EmptyState>
                    ) : (
                        <div
                            aria-label={copy.title}
                            role="img"
                            style={{ height: BAR_CHART_HEIGHT_PX }}
                            {...getNodeTestIdProp('hourly-activity-chart')}
                        >
                            <BarChart
                                data={
                                    data.hours as unknown as Record<
                                        string,
                                        number | string
                                    >[]
                                }
                                height={BAR_CHART_HEIGHT_PX}
                                width="100%"
                            >
                                <BarChart.Grid
                                    stroke="var(--surface-tertiary)"
                                    strokeDasharray="3 3"
                                    vertical={false}
                                />
                                <BarChart.XAxis
                                    axisLine={false}
                                    dataKey="hour"
                                    tick={{
                                        fill: 'var(--muted)',
                                        fontSize: 11,
                                    }}
                                    tickFormatter={formatHour}
                                    tickLine={false}
                                />
                                <BarChart.YAxis hide />
                                <BarChart.Tooltip
                                    content={
                                        <HourlyActivityTooltip
                                            valueFormatter={formatRevenue}
                                        />
                                    }
                                    cursor={{
                                        fill: 'var(--surface-secondary)',
                                    }}
                                />
                                <BarChart.Bar
                                    dataKey="totalSales"
                                    isAnimationActive={false}
                                    radius={[4, 4, 0, 0]}
                                    shape={renderBar}
                                />
                            </BarChart>
                        </div>
                    )}
                </Widget.Content>
            </Widget>
        </section>
    );
};
